import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { CALENDLY, NAV } from "@/data";
import { Logo } from "@/components/primitives";

export const NavLink = ({ item, className, onClick, children }) => {
  const { pathname } = useLocation();
  const home = pathname === "/";
  const isRoute = item.href.startsWith("/");
  const active = isRoute && pathname.startsWith(item.href);
  const testId = `nav-link-${item.href.replace(/^[/#]+/, "")}`;
  const cls = `${className} ${active ? "text-[var(--qd-accent)]" : ""}`;
  if (isRoute || !home) {
    return <Link to={isRoute ? item.href : `/${item.href}`} onClick={onClick} className={cls} data-testid={testId}>{children || item.label}</Link>;
  }
  return <a href={item.href} onClick={onClick} className={cls} data-testid={testId}>{children || item.label}</a>;
};

const ProductMenu = ({ item, linkCls }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid="nav-product-menu">
      <button className={`${linkCls} inline-flex items-center gap-1`} onClick={() => setOpen(true)} aria-expanded={open} aria-haspopup="true" data-testid="nav-link-product">
        {item.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.2 }} className="absolute left-0 top-full pt-3" data-testid="nav-product-dropdown">
            <div className="w-80 rounded-2xl bg-white border border-[var(--qd-line)] shadow-xl p-2">
              {item.children.map((c) => (
                <NavLink key={c.href} item={c} onClick={() => setOpen(false)} className="block rounded-xl px-4 py-3 hover:bg-[var(--qd-bg-2)] transition-colors">
                  <span className="block text-sm font-semibold text-[var(--qd-ink)]">{c.label}</span>
                  <span className="block text-xs text-[var(--qd-muted)] mt-0.5 leading-snug">{c.desc}</span>
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const linkCls = "px-3.5 py-2 text-sm font-medium text-[var(--qd-ink)] hover:text-[var(--qd-accent)] rounded-full hover:bg-white/70 transition-colors";
  return (
    <motion.header initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }} className="fixed top-0 inset-x-0 z-50 px-4 pt-4" data-testid="nav">
      <div className={`mx-auto max-w-7xl rounded-2xl px-4 sm:px-5 py-3 flex items-center justify-between transition-all duration-500 ${scrolled ? "glass glass-blur shadow-lg" : "bg-transparent border border-transparent"}`}>
        <Link to="/" aria-label="Quantum Desk home" data-testid="nav-logo">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map((n) => (n.children ? <ProductMenu key={n.label} item={n} linkCls={linkCls} /> : <NavLink key={n.href} item={n} className={linkCls} />))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="btn-primary !py-2.5 !px-3.5 sm:!px-4 text-xs sm:text-sm whitespace-nowrap" data-testid="nav-book-demo">
            Book a demo
          </a>
          <button className="lg:hidden p-2 rounded-full hover:bg-white/60" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="lg:hidden mx-auto max-w-7xl mt-2 glass rounded-2xl p-3 grid grid-cols-2 gap-1" data-testid="nav-mobile-menu">
            {NAV.flatMap((n) => (n.children ? n.children.map((c) => ({ ...c, label: `Product · ${c.label}` })) : [n])).map((n) => (
              <NavLink key={n.href} item={n} onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-white/70" />
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
